import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { extractCityAndCountry } from '../../util/location';

import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

import { AspectRatioWrapper, Button, NamedLink, ResponsiveImage, SecondaryButton } from '../../components';

import css from './ListingCard.module.css';
import { DDG_UNIT_TYPE_EVENT } from '../../util/ddgConstants';

const MIN_LENGTH_FOR_LONG_WORDS = 16;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: DDG_UNIT_TYPE_EVENT }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    onViewAuthorClick,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);

  const id = currentListing.id.uuid;
  let { title = '', price, publicData } = currentListing.attributes;

  // Hydrate price if it's a plain object (from custom API)
  if (price && !(price instanceof Money) && price._sdkType === 'Money') {
    price = new Money(price.amount, price.currency);
  }

  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  // Try to get listing-card variants first, fall back to scaled variants if not available
  let variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];
  // Fallback to scaled variants if listing-card variants aren't available
  if (variants.length === 0 && firstImage?.attributes?.variants) {
    variants = Object.keys(firstImage.attributes.variants).filter(k => k.startsWith('scaled'));
  }

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  // Only show rating if there are reviews
  const hasReviews = publicData?.reviews?.length > 0;
  const rating = hasReviews ? publicData?.rating : null;

  // Get location from public data and extract city and country
  const locationString = publicData?.location?.address || null;
  const cityAndCountry = extractCityAndCountry(locationString);

  const handleAuthorIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to shop storefront page using author displayName slug
    const authorSlug = createSlug(authorName);
    window.location.href = `/shop/${authorSlug}`;
  }

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        {showAuthorInfo && (
          <SecondaryButton
            className={css.providerLabel}
            onClick={handleAuthorIconClick}
          >
            <span className={css.providerIcon}>✨</span>
            <FormattedMessage id="ListingCard.author" values={{ authorName }} />
          </SecondaryButton>
        )}
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        {showAuthorInfo && author.profileImage && (
          <div className={css.providerLogo}>
            <ResponsiveImage
              rootClassName={css.providerLogoImage}
              alt={authorName}
              image={author.profileImage}
              variants={['scaled-small', 'scaled-medium']}
              sizes="40px"
            />
          </div>
        )}
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {cityAndCountry ? (
            <div className={css.location}>{cityAndCountry}</div>
          ) : null}
          {rating ? (
            <div className={css.rating}>
              <span className={css.star}>★</span>
              <span className={css.ratingValue}>{rating.toFixed(2)}</span>
            </div>
          ) : null}
        </div>
        {price ? (
          <div className={css.price}>
            <div className={css.priceValue}>{formatMoney(intl, price)}</div>
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
